import { Component, computed, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { DatabaseTabs, DatabaseTabsComponent } from '../../../../components/database-tabs/database-tabs.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmDialogModel, ConfirmDialogService, UiButtonComponent, UiModels, UiNoDataComponent, UiPersonTileComponent, UiSearchComponent, UiSelectedItemsSnackbarComponent, } from '@ui';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { PersonActions } from '@states/person/person.actions';
import { personFeature } from '@states/person/person.feature';
import { syncQueryParam } from '../../../../../../helpers/sync-query-param.helper';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, filter, map, take } from 'rxjs';
import { SharedModels } from '@share-models';
import { UiPreloaderComponent } from '../../../../../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { features } from '@consts/text.const';
import { PersonService } from '../../../../../../services/person.service';
import { PersonModel } from '@models/person.model';
import { PersonDialogService } from '../../../../../../services/person-dialog.service';
import { Actions, ofType } from '@ngrx/effects';
import { PersonGroupsModel } from '@models/person-groups.model';

enum PersonTab {
  saved = 'saved',
  unsaved = 'unsaved',
}

@Component({
  selector: 'app-persons',
  imports: [
    DatabaseTabsComponent,
    TranslatePipe,
    UiButtonComponent,
    UiSearchComponent,
    ReactiveFormsModule,
    UiPersonTileComponent,
    UiPreloaderComponent,
    UiNoDataComponent,
    FormsModule,
    UiSelectedItemsSnackbarComponent,
    MatTooltip,
    MatSelect,
    MatOption,
  ],
  templateUrl: './persons.component.html',
  styleUrl: './persons.component.scss',
})
export class PersonsComponent implements OnInit {
  private readonly store$ = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly personService = inject(PersonService);
  private readonly personDialogService = inject(PersonDialogService);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly translate = inject(TranslateService);
  private readonly actions$ = inject(Actions);

  protected readonly personTab = PersonTab;
  protected readonly personTrainingStatus = PersonModel.TrainingStatus;
  private readonly savedPersons = this.store$.selectSignal(personFeature.selectAllSavedPersons);
  private readonly allUnsavedPersons = this.store$.selectSignal(personFeature.selectAllUnsavedPersons);
  private readonly loadedImageBucketIds = signal<Set<number>>(new Set<number>());
  private readonly failedImageBucketIds = signal<Set<number>>(new Set<number>());
  private readonly allUnsavedImagesResolved = computed(() => {
    const all = this.allUnsavedPersons();
    if (all.length === 0) return true;
    return this.loadedImageBucketIds().size + this.failedImageBucketIds().size >= all.length;
  });
  protected readonly unsavedPersons = computed(() => {
    const failedIds = this.failedImageBucketIds();
    return failedIds.size > 0
      ? this.allUnsavedPersons().filter((person) => !failedIds.has(person.bucketId))
      : this.allUnsavedPersons();
  });
  protected readonly savedPersonsCounter = this.store$.selectSignal(personFeature.selectSavedPersonsTotal);
  private readonly storeUnsavedPersonsCounter = this.store$.selectSignal(personFeature.selectUnsavedPersonsTotal);
  protected readonly unsavedPersonsCounter = computed(() => {
    if (this.allUnsavedImagesResolved()) {
      return this.unsavedPersons().length;
    }
    return this.storeUnsavedPersonsCounter();
  });
  protected readonly isSavedPersonsLoading = this.store$.selectSignal(personFeature.selectIsLoadingSavedPersons);
  protected readonly isBulkDeletingSavedPersons = this.store$.selectSignal(personFeature.selectIsBulkDeletingSavedPersons);
  protected readonly isUnsavedPersonsLoading = this.store$.selectSignal(personFeature.selectIsLoadingUnsavedPersons);
  protected readonly personPageTabs: DatabaseTabs<PersonTab>[] = [
    {
      value: PersonTab.saved,
      labelKey: 'PERSONS.SAVED_PERSONS',
      iconName: 'ui-icon-users',
      count: this.savedPersonsCounter,
    },
    {
      value: PersonTab.unsaved,
      labelKey: 'PERSONS.UNSAVED_PERSONS',
      iconName: 'ui-icon-add-person',
      count: this.unsavedPersonsCounter,
    },
  ];
  protected readonly viewState = SharedModels.ViewState;
  protected readonly preloaderColor = UiModels.PreloaderColor;
  protected readonly noData = features.person;
  protected readonly currentTab = syncQueryParam<PersonTab>('tab', { defaultValue: PersonTab.saved });
  protected readonly selectedPersonIds = signal<Set<number>>(new Set<number>());
  protected readonly selectedPersonsCount = computed(() => this.selectedPersonIds().size);
  protected readonly confirmingPersonIds = this.store$.selectSignal(personFeature.selectSavedPersonConfirmingIds);
  protected readonly deletingPersonIds = this.store$.selectSignal(personFeature.selectSavedPersonDeletingIds);
  protected readonly searchForm = new FormControl<string>('', { nonNullable: true });
  private readonly searchValue = syncQueryParam('search', { defaultValue: '' });
  private readonly syncSearchFormWithQueryParam = effect(() => {
    const searchValue = this.searchValue() ?? '';
    if (this.searchForm.value !== searchValue) {
      this.searchForm.setValue(searchValue, { emitEvent: false });
    }
  });
  protected readonly trainingStatusForm = new FormControl<PersonModel.TrainingStatus | null>(null);
  private readonly trainingStatusValue = toSignal(this.trainingStatusForm.valueChanges, {
    initialValue: this.trainingStatusForm.value,
  });
  protected readonly filteredSavedPersons = computed(() => {
    const searchValue = this.searchValue()?.trim();
    const trainingStatus = this.trainingStatusValue();
    let savedPersons = this.savedPersons();
    if (searchValue && savedPersons.length > 0) {
      savedPersons = savedPersons.filter((person) => person.name.toLowerCase().includes(searchValue.toLowerCase()));
    }
    if (trainingStatus !== null) {
      savedPersons = savedPersons.filter((person) => person.trainingStatus === trainingStatus);
    }
    return savedPersons;
  });
  protected readonly savedPersonViewState = computed<SharedModels.ViewState>(() => {
    const isLoading = this.isSavedPersonsLoading();
    const searchValue = this.searchValue();
    const trainingStatus = this.trainingStatusValue();
    const persons = this.savedPersons();
    const filteredPersons = this.filteredSavedPersons();
    const hasActiveFilter = Boolean(searchValue) || trainingStatus !== null;
    if (isLoading) return SharedModels.ViewState.INITIAL_EMPTY;
    else if (hasActiveFilter && !filteredPersons.length) return SharedModels.ViewState.NO_RESULTS;
    else if (!isLoading && persons.length) return SharedModels.ViewState.HAS_CONTENT;
    else return SharedModels.ViewState.INITIAL_EMPTY;
  });
  protected readonly unsavedPersonViewState = computed<SharedModels.ViewState>(() => {
    const isLoading = this.isUnsavedPersonsLoading();
    const persons = this.unsavedPersons();
    if (isLoading) return SharedModels.ViewState.INITIAL_EMPTY;
    else if (!isLoading && persons.length) return SharedModels.ViewState.HAS_CONTENT;
    else return SharedModels.ViewState.INITIAL_EMPTY;
  });

  public ngOnInit(): void {
    this.store$.dispatch(PersonActions.getSavedPersons());
    this.store$.dispatch(PersonActions.getUnsavedPersons());

    this.searchForm.valueChanges
      .pipe(
        debounceTime(500),
        map((value) => value.trim()),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => this.searchValue.set(value));

    this.actions$
      .pipe(
        ofType(PersonActions.createPersonSuccess, PersonActions.bulkCreatePendingPersonsSuccess),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.clearSelectedPersons());
  }

  protected changeActiveTab(tab: PersonTab): void {
    if (this.isBulkDeletingSavedPersons()) {
      return;
    }

    this.clearSelectedPersonsInternal();
    this.searchValue.set('');
    this.trainingStatusForm.setValue(null);
    this.currentTab.set(tab);
  }

  protected getImagePath(person: PersonModel.SavedPerson | PersonModel.UnsavedPerson, gcp: boolean = true): string {
    const isSaved = 'personId' in person;
    return this.personService.getPersonImagePath(person.bestImage, isSaved, gcp);
  }

  protected openSavePersonsDialog(): void {
    if (this.isBulkDeletingSavedPersons()) {
      return;
    }

    const selectedUnsavedPersons = this.unsavedPersons()
      .filter((person) => this.selectedPersonIds().has(person.bucketId));

    this.personDialogService.openSavePersons(selectedUnsavedPersons);
  }

  protected openEditPersonDialog(person: PersonModel.SavedPerson): void {
    this.personDialogService.openEditPerson(person);
  }

  protected openAddPersonToGroupsDialog(): void {
    if (this.isBulkDeletingSavedPersons()) {
      return;
    }

    const personIds: number[] = this.currentTab() === PersonTab.saved ? Array.from(this.selectedPersonIds()) : [];
    const unsavedPersonDtos: PersonModel.UnsavedPersonDto[] = this.currentTab() === PersonTab.unsaved
      ? Array.from(this.selectedPersonIds()).map((personId, index) => ({
          bucketId: personId,
          name: PersonModel.getDefaultPersonName(index + 1),
        }))
      : [];

    const data: PersonGroupsModel.AddPersonToGroupsDialogData = {
      personType: this.currentTab() === PersonTab.saved
        ? PersonGroupsModel.AddPersonToGroupDialogTab.SavedPersons
        : PersonGroupsModel.AddPersonToGroupDialogTab.UnsavedPersons,
      unsavedPersonDtos,
      personIds,
    };

    this.personDialogService
      .openAddPersonToGroups(data)
      .afterClosed()
      .pipe(
        filter(Boolean),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.clearSelectedPersonsInternal());
  }

  protected openAddPersonByImageDialog(): void {
    this.personDialogService.openAddPersonByImage();
  }

  protected openConfirmRecognitionDialog(person: PersonModel.UnsavedPerson): void {
    this.personDialogService
      .openConfirmRecognition({ bucketId: person.bucketId })
      .afterClosed()
      .pipe(
        filter(Boolean),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((recognitionResult) => {
        this.personDialogService.openSavePerson({ person, recognitionResult });
      });
  }

  protected openConfirmSavedPersonRecognitionDialog(person: PersonModel.SavedPerson): void {
    if (
      person.trainingStatus === PersonModel.TrainingStatus.Trained ||
      this.isPersonConfirming(person.personId)
    ) {
      return;
    }

    this.personDialogService
      .openConfirmRecognition({ personId: person.personId })
      .afterClosed()
      .pipe(
        filter(Boolean),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((recognitionResult) => {
        this.store$.dispatch(PersonActions.trainSavedPerson({
          request: {
            personId: person.personId,
            positiveTrackerIds: recognitionResult.positiveTrackerIds,
            negativeTrackerIds: recognitionResult.negativeTrackerIds,
          },
        }));
      });
  }

  protected isPersonConfirming(personId: number): boolean {
    return this.confirmingPersonIds().includes(personId);
  }

  protected openBulkDeleteConfirmDialog(person?: PersonModel.SavedPerson): void {
    if (
      this.isBulkDeletingSavedPersons() ||
      this.currentTab() !== PersonTab.saved ||
      (!person && this.selectedPersonsCount() === 0)
    ) {
      return;
    }

    const personIds = person ? [person.personId] : Array.from(this.selectedPersonIds());
    const isSinglePerson = personIds.length === 1;
    const personName =
      person?.name ?? this.savedPersons().find((savedPerson) => savedPerson.personId === personIds[0])?.name;
    const msg = this.translate
      .instant(isSinglePerson ? 'PERSONS.DELETE_PERSON_CONFIRM_MESSAGE' : 'PERSONS.DELETE_PEOPLE_CONFIRM_MESSAGE', {
        peopleCount: personIds.length,
        personName,
      })
      .replace(/\n/g, '<br>');

    this.confirmDialog
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: this.translate.instant(
          isSinglePerson ? 'PERSONS.DELETE_PERSON_CONFIRM_TITLE' : 'PERSONS.DELETE_PEOPLE_CONFIRM_TITLE',
        ),
        msg,
        confirm: this.translate.instant('COMMON.DELETE'),
        cancel: this.translate.instant('COMMON.CANCEL'),
        disableClose: true,
      })
      .pipe(
        take(1),
        filter(
          (result) =>
            result?.selection === ConfirmDialogModel.ConfirmResult.OK &&
            result.type === ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.clearSelectionOnBulkDeleteSuccess(personIds);
        this.store$.dispatch(PersonActions.bulkDeleteSavedPersons({ personIds }));
      });
  }

  protected onUnsavedPersonImageLoaded(bucketId: number): void {
    this.loadedImageBucketIds.update((ids) => {
      const next = new Set(ids);
      next.add(bucketId);
      return next;
    });
  }

  protected onUnsavedPersonImageError(bucketId: number): void {
    this.failedImageBucketIds.update((ids) => {
      const next = new Set(ids);
      next.add(bucketId);
      return next;
    });
  }

  protected isUnsavedPersonVisible(bucketId: number): boolean {
    return this.loadedImageBucketIds().has(bucketId);
  }

  protected isPersonSelected(personId: number): boolean {
    return this.selectedPersonIds().has(personId);
  }

  protected isPersonDeleting(personId: number): boolean {
    return this.deletingPersonIds().includes(personId);
  }

  protected updatePersonSelection(personId: number, selected: boolean): void {
    this.selectedPersonIds.update((selectedPersonIds) => {
      const nextSelectedPersonIds = new Set(selectedPersonIds);

      if (selected) {
        nextSelectedPersonIds.add(personId);
      } else {
        nextSelectedPersonIds.delete(personId);
      }

      return nextSelectedPersonIds;
    });
  }

  protected selectAllDisplayedPersons(): void {
    if (this.isBulkDeletingSavedPersons()) {
      return;
    }

    const displayedPersonIds = this.getDisplayedPersonIds();

    this.selectedPersonIds.update((selectedPersonIds) => new Set([...selectedPersonIds, ...displayedPersonIds]));
  }

  protected clearSelectedPersons(): void {
    if (this.isBulkDeletingSavedPersons()) {
      return;
    }

    this.clearSelectedPersonsInternal();
  }

  private clearSelectedPersonsInternal(): void {
    this.selectedPersonIds.set(new Set<number>());
  }

  private clearSelectionOnBulkDeleteSuccess(personIds: number[]): void {
    this.actions$
      .pipe(
        ofType(PersonActions.bulkDeleteSavedPersonsSuccess),
        filter((action) => this.samePersonIds(action.personIds, personIds)),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.clearSelectedPersonsInternal();
      });
  }

  private samePersonIds(left: number[], right: number[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    const sortedLeft = [...left].sort((a, b) => a - b);
    const sortedRight = [...right].sort((a, b) => a - b);

    return sortedLeft.every((id, index) => id === sortedRight[index]);
  }

  private getDisplayedPersonIds(): number[] {
    if (this.currentTab() === PersonTab.saved) {
      return this.filteredSavedPersons().map((person) => person.personId);
    }

    return this.unsavedPersons().map((person) => person.bucketId);
  }
}
